import React, { useState, useEffect, useRef } from "react";
import "./RadioButton.scss";

// Custom Typography component
import { L } from "../../../ui/Typography"; 

const RadioButton = ({
  id, // Unique ID for the input (for label association)
  name, // Radio group name (for grouping multiple buttons)
  value, // Value associated with this radio button
  label, // Label text shown to the user
  size = "md", // Size variant for styling: 'sm', 'md', etc.
  disabled = false, // If true, disables the button
  defaultChecked = false, // If true, this radio button is selected initially
  className = "", // Additional custom classes
  onChange = () => {}, // Callback function when selected
}) => {
  /* === useState === */
  const [isChecked, setIsChecked] = useState(defaultChecked); // Tracks whether this radio button is currently selected
  /* === End useState === */

  /* === useRef === */
  const inputRef = useRef(null); // Reference to the native <input> element to programmatically set its checked state
  /* === End useRef === */

  useEffect(() => {
    const handler = (e) => {
      if (e.detail.name === name && e.detail.value !== value) {
        setIsChecked(false); // Uncheck this radio button if it's not the selected one
      }
    };

    window.addEventListener("radio-change", handler);
    return () => window.removeEventListener("radio-change", handler); // Cleanup on unmount
  }, [name, value]); // Listen for custom "radio-change" event to uncheck this radio if another one in the group is selected

  /* === Event Handler === */
  const handleClick = () => {
    if (disabled) return;

    setIsChecked(true); // Update internal checked state
    inputRef.current.checked = true; // Reflect selection on native input
    onChange(value); // Trigger onChange callback with this button's value

    window.dispatchEvent(
      new CustomEvent("radio-change", { detail: { name, value } })
    ); // Notify other radio buttons in the same group to uncheck themselves
  };
  /* === End Event Handler === */

  return (
    <div
      className={`custom-radio-wrapper ${className} ${size} ${
        disabled ? "disabled" : ""
      } ${isChecked ? "checked" : ""}`}
      onClick={handleClick}
    >
      <input
        type="radio"
        ref={inputRef} // Ref for programmatic manipulation
        id={id}
        name={name}
        value={value}
        defaultChecked={defaultChecked}
        disabled={disabled}
        style={{ display: "none" }} // Hide native radio input (custom UI used)
      />

      {/* Custom styled radio UI */}
      <div className="borderWrapper">
        <div className="custom-radio-parent borderInner">
          <div className="custom-radio borderWrapperJustOneCorner"></div>
        </div>
      </div>

      {/* Label using custom Typography component */}
      <L htmlFor={id} className="radio-label">
        {label}
      </L>
    </div>
  );
};

export default RadioButton;
